import { escapeHtml } from "../editor/escapeHtml.js";

/** Safe ASCII-ish filename stem (extension added by caller). */
export function sanitizePdfFilename(stem: string): string {
  const t = stem
    .trim()
    .replace(/[/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/-+/g, "-");
  return (t || "nullapad-export").slice(0, 120);
}

export type ExportPdfLayout = "fit-content" | "a4-multi";

export type ExportPdfSettings = {
  layout: ExportPdfLayout;
  orientation: "portrait" | "landscape";
  /** 1-based inclusive; only used when layout is a4-multi */
  pageFrom: number;
  pageTo: number;
};

export type ElementCaptureStyleBackup = {
  height: string;
  maxHeight: string;
  overflow: string;
  width: string;
};

/** How wide to expand the element before measuring / rasterizing. */
export type CaptureWidthMode = "full" | "viewport-biased";

/**
 * Outer width (px) for PDF capture after overflow is visible.
 * `viewport-biased` caps pathological `scrollWidth` (e.g. one huge line) so previews stay readable
 * and match the exported raster; `full` uses the full scroll width.
 */
export function captureOuterWidthPx(el: HTMLElement, mode: CaptureWidthMode): number {
  const cw = Math.max(1, Math.round(el.clientWidth));
  const sw = Math.max(el.scrollWidth, cw);
  if (mode === "full") return sw;
  const cap = Math.max(2048, Math.round(cw * 10));
  return Math.max(cw, Math.min(sw, cap));
}

export function backupAndExpandElementForCapture(el: HTMLElement): ElementCaptureStyleBackup {
  return {
    height: el.style.height,
    maxHeight: el.style.maxHeight,
    overflow: el.style.overflow,
    width: el.style.width,
  };
}

export function applyExpandedCaptureStyles(el: HTMLElement, widthMode: CaptureWidthMode = "full"): void {
  el.style.height = "auto";
  el.style.maxHeight = "none";
  el.style.overflow = "visible";
  el.style.width = `${captureOuterWidthPx(el, widthMode)}px`;
}

export function restoreElementCaptureStyles(el: HTMLElement, b: ElementCaptureStyleBackup): void {
  el.style.height = b.height;
  el.style.maxHeight = b.maxHeight;
  el.style.overflow = b.overflow;
  el.style.width = b.width;
}

async function doubleRaf(): Promise<void> {
  await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
}

export type RenderElementToCanvasOptions = {
  captureWidthMode?: CaptureWidthMode;
};

/** Rasterize DOM (expanded to full scroll size) at the given html2canvas scale. */
export async function renderElementToCanvas(
  element: HTMLElement,
  scale: number,
  opts?: RenderElementToCanvasOptions,
): Promise<HTMLCanvasElement> {
  const widthMode = opts?.captureWidthMode ?? "full";
  const backup = backupAndExpandElementForCapture(element);
  applyExpandedCaptureStyles(element, widthMode);
  await doubleRaf();
  try {
    const { default: html2canvas } = await import("html2canvas");
    return await html2canvas(element, {
      scale,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: "#ffffff",
      width: element.scrollWidth,
      height: element.scrollHeight,
    });
  } finally {
    restoreElementCaptureStyles(element, backup);
  }
}

const A4_MM = { w: 210, h: 297 };

/** Content box inside A4 with margins (mm). */
export function a4ContentSizeMm(orientation: "portrait" | "landscape", marginMm: number): { cw: number; ch: number } {
  if (orientation === "landscape") {
    return { cw: A4_MM.h - 2 * marginMm, ch: A4_MM.w - 2 * marginMm };
  }
  return { cw: A4_MM.w - 2 * marginMm, ch: A4_MM.h - 2 * marginMm };
}

/**
 * How many vertical slices are needed when the image is scaled to content width
 * and split across A4 pages.
 */
export function computeA4PageCount(
  canvas: HTMLCanvasElement,
  orientation: "portrait" | "landscape",
  marginMm = 10,
): number {
  const { cw, ch } = a4ContentSizeMm(orientation, marginMm);
  if (canvas.width <= 0) return 1;
  const imgTotalHmm = (canvas.height / canvas.width) * cw;
  return Math.max(1, Math.ceil(imgTotalHmm / ch));
}

/**
 * Writes `canvas` to a PDF file using jsPDF. No extra browser tab.
 */
export async function exportCanvasToPdf(
  canvas: HTMLCanvasElement,
  filenameStem: string,
  settings: ExportPdfSettings,
): Promise<void> {
  const { jsPDF } = await import("jspdf");
  const data = canvas.toDataURL("image/png");
  const safeName = sanitizePdfFilename(filenameStem);

  if (settings.layout === "fit-content") {
    const w = canvas.width;
    const h = canvas.height;
    const orientation = w >= h ? "l" : "p";
    const pdf = new jsPDF({ orientation, unit: "px", format: [w, h], compress: true });
    pdf.addImage(data, "PNG", 0, 0, w, h);
    pdf.save(`${safeName}.pdf`);
    return;
  }

  const marginMm = 10;
  const o = settings.orientation === "landscape" ? "l" : "p";
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: o, compress: true });
  const { cw: contentW, ch: contentH } = a4ContentSizeMm(settings.orientation, marginMm);
  const imgTotalHmm = (canvas.height / canvas.width) * contentW;
  const pageCount = Math.max(1, Math.ceil(imgTotalHmm / contentH));

  const from = Math.max(1, Math.min(settings.pageFrom, pageCount));
  const to = Math.max(from, Math.min(settings.pageTo, pageCount));

  let first = true;
  for (let p = 1; p <= pageCount; p++) {
    if (p < from || p > to) continue;

    const yOffsetMm = (p - 1) * contentH;
    const sliceHmm = Math.min(contentH, imgTotalHmm - yOffsetMm);
    const sy = Math.min(
      canvas.height - 1e-6,
      Math.max(0, (yOffsetMm / imgTotalHmm) * canvas.height),
    );
    let sHeight = (sliceHmm / imgTotalHmm) * canvas.height;
    sHeight = Math.max(0, Math.min(sHeight, canvas.height - sy));

    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = Math.max(1, Math.round(sHeight));
    const ctx = slice.getContext("2d");
    if (!ctx) throw new Error("Could not get canvas context");
    ctx.drawImage(canvas, 0, sy, canvas.width, sHeight, 0, 0, canvas.width, slice.height);

    if (!first) pdf.addPage("a4", o);
    first = false;
    pdf.addImage(slice.toDataURL("image/png"), "PNG", marginMm, marginMm, contentW, sliceHmm);
  }

  pdf.save(`${safeName}.pdf`);
}

/** Build a static HTML table from the live CSV grid (inputs → plain cell text). */
export function csvTableHostToStaticHtml(host: HTMLElement): string {
  const table = host.querySelector("table.npp-csv-table");
  if (!table) return `<p>${escapeHtml("(No table to export)")}</p>`;
  const parts: string[] = ['<table class="npp-export-csv">'];
  for (const tr of table.querySelectorAll("tr")) {
    parts.push("<tr>");
    for (const td of tr.querySelectorAll("td")) {
      const inp = td.querySelector("input");
      const v = inp instanceof HTMLInputElement ? inp.value : td.textContent ?? "";
      parts.push(`<td>${escapeHtml(v)}</td>`);
    }
    parts.push("</tr>");
  }
  parts.push("</table>");
  return parts.join("");
}
